import React, { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@components/layout/DashboardLayout';
import { ProfileInfoForm } from '@components/profile/ProfileInfoForm';
import { ChangePasswordForm } from '@components/profile/ChangePasswordForm';
import { Spinner } from '@components/common/Spinner';
import { InvestorProfileWizard } from '@components/investor-profile/InvestorProfileWizard';
import { PlanGate } from '@components/PlanGate';
import { authService } from '@services/auth-service';
import { investorProfileService } from '@services/investor-profile.service';
import { User, UserRole, InvestorProfile } from '@/types/index';

const ROLE_COLORS: Record<UserRole, string> = {
  FREEMIUM: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  PREMIUM: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  PRO: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  ADMIN: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

type ApiKey = { id: string; name: string; keyPrefix: string; isActive: boolean; lastUsedAt: string | null; createdAt: string };

type TabId = 'profile' | 'investor' | 'settings';

export const Profile: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const tab = searchParams.get('tab');
    if (tab === 'investor') return 'investor';
    if (tab === 'settings') return 'settings';
    return 'profile';
  });

  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [investorProfile, setInvestorProfile] = useState<InvestorProfile | null>(null);
  const [isInvestorLoading, setIsInvestorLoading] = useState(false);
  const [investorError, setInvestorError] = useState<string | null>(null);

  // Settings tab state
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [createdKey, setCreatedKey] = useState<{ rawKey: string; name: string } | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);

  const loadApiKeys = async () => {
    try {
      const keys = await authService.listApiKeys();
      setApiKeys(keys ?? []);
    } catch {
      // silently fail
    }
  };

  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setIsCreatingKey(true);
    setKeyError(null);
    try {
      const result = await authService.createApiKey(newKeyName.trim());
      setCreatedKey({ rawKey: result.rawKey, name: result.name });
      setNewKeyName('');
      await loadApiKeys();
    } catch (err: any) {
      setKeyError(err?.message || 'Erreur lors de la création.');
    } finally {
      setIsCreatingKey(false);
    }
  };

  const handleRevokeApiKey = async (id: string) => {
    if (!confirm('Révoquer cette clé ? Les automatisations utilisant cette clé cesseront de fonctionner.')) return;
    try {
      await authService.revokeApiKey(id);
      await loadApiKeys();
    } catch (err: any) {
      alert(err?.message || 'Erreur lors de la révocation.');
    }
  };

  useEffect(() => {
    authService.getProfile()
      .then(setUser)
      .catch(() => setError('Erreur lors du chargement du profil'))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab === 'investor' && !investorProfile && !isInvestorLoading) {
      setIsInvestorLoading(true);
      investorProfileService.get()
        .then(setInvestorProfile)
        .catch(() => setInvestorError('Impossible de charger le profil investisseur'))
        .finally(() => setIsInvestorLoading(false));
    }
    if (activeTab === 'settings') {
      loadApiKeys();
    }
  }, [activeTab]);

  const switchTab = (tab: TabId) => {
    setActiveTab(tab);
    const param: Record<string, string> = tab === 'profile' ? {} : { tab };
    setSearchParams(param, { replace: true });
  };

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('fr-CH', { year: 'numeric', month: 'long', day: 'numeric' });

  if (isLoading) {
    return (
      <DashboardLayout title="Mon profil">
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !user) {
    return (
      <DashboardLayout title="Mon profil">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{error || 'Erreur lors du chargement du profil'}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Mon profil">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 space-y-6">
        {/* En-tête */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Mon profil</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            Gérez vos informations personnelles et votre profil investisseur
          </p>
        </div>

        {/* Onglets */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex overflow-x-auto scrollbar-hide">
            {([
              { id: 'profile' as TabId, labelShort: 'Profil', label: 'Informations personnelles' },
              { id: 'investor' as TabId, labelShort: 'Investisseur', label: 'Profil investisseur' },
              { id: 'settings' as TabId, labelShort: 'Paramètres', label: 'Paramètres' },
            ] as const).map(({ id, labelShort, label }) => (
              <button
                key={id}
                onClick={() => switchTab(id)}
                className={`flex-none pb-3 px-1 mr-6 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === id
                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                <span className="sm:hidden">{labelShort}</span>
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Onglet 1 : Informations personnelles */}
        {activeTab === 'profile' && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Informations personnelles
              </h2>
              <ProfileInfoForm user={user} onSuccess={() => authService.getProfile().then(setUser)} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Sécurité</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Changez votre mot de passe pour sécuriser votre compte
              </p>
              <ChangePasswordForm onSuccess={() => {}} />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Informations du compte
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Compte créé le
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDate(user.createdAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Type de compte
                  </label>
                  <div className="mt-1 flex items-center gap-3 flex-wrap">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[user.role ?? 'FREEMIUM']}`}>
                      {user.role ?? 'FREEMIUM'}
                    </span>
                    {(() => {
                      if (!user.planExpiresAt) return null;
                      const daysLeft = Math.ceil((new Date(user.planExpiresAt).getTime() - Date.now()) / 86400000);
                      if (daysLeft <= 0) {
                        return (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                            Période d'essai expirée
                          </span>
                        );
                      }
                      const color = daysLeft <= 7
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
                      return (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
                          Période d'essai — {daysLeft} jour{daysLeft > 1 ? 's' : ''} restant{daysLeft > 1 ? 's' : ''}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Identifiant
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 font-mono">{user.id}</p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Onglet 2 : Profil investisseur */}
        {activeTab === 'investor' && (
          <div>
            {isInvestorLoading && (
              <div className="flex items-center justify-center py-16">
                <Spinner />
              </div>
            )}
            {investorError && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400">
                {investorError}
              </div>
            )}
            {investorProfile && !isInvestorLoading && (
              <InvestorProfileWizard
                initialProfile={investorProfile}
                onSaved={setInvestorProfile}
              />
            )}
          </div>
        )}

        {/* Onglet 3 : Paramètres */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Clés API — Admin uniquement */}
            <PlanGate minRole="ADMIN">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Clés API
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Tokens permanents pour les intégrations machine-to-machine (n8n, scripts). Utilisez le header{' '}
                  <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">X-API-Key: immo_xxxx</code>.
                </p>

                {/* Formulaire création */}
                <form onSubmit={handleCreateApiKey} className="flex gap-2 mb-4">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nom de la clé (ex: n8n - import pipeline)"
                    value={newKeyName}
                    onChange={e => setNewKeyName(e.target.value)}
                    minLength={3}
                  />
                  <button
                    type="submit"
                    disabled={isCreatingKey || !newKeyName.trim()}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50 whitespace-nowrap"
                  >
                    {isCreatingKey ? 'Création...' : 'Créer une clé'}
                  </button>
                </form>

                {keyError && (
                  <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">{keyError}</div>
                )}

                {/* Affichage unique de la clé créée */}
                {createdKey && (
                  <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">
                      ⚠ Copiez cette clé maintenant — elle ne sera plus affichée.
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">Clé créée : <strong>{createdKey.name}</strong></p>
                    <div className="flex gap-2">
                      <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-900 border border-amber-300 rounded text-sm font-mono text-gray-900 dark:text-gray-100 break-all">
                        {createdKey.rawKey}
                      </code>
                      <button
                        onClick={() => { navigator.clipboard.writeText(createdKey.rawKey); setKeyCopied(true); setTimeout(() => setKeyCopied(false), 2000); }}
                        className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm font-medium whitespace-nowrap"
                      >
                        {keyCopied ? 'Copié !' : 'Copier'}
                      </button>
                    </div>
                    <button onClick={() => setCreatedKey(null)} className="mt-2 text-xs text-amber-600 dark:text-amber-400 underline">
                      J'ai copié la clé, fermer
                    </button>
                  </div>
                )}

                {/* Liste des clés */}
                {apiKeys.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">Aucune clé API créée.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700 text-left">
                          <th className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 pb-2">Nom</th>
                          <th className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 pb-2">Préfixe</th>
                          <th className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 pb-2">Dernière utilisation</th>
                          <th className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 pb-2">Statut</th>
                          <th className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 pb-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {apiKeys.map(key => (
                          <tr key={key.id}>
                            <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">{key.name}</td>
                            <td className="py-2 pr-4 font-mono text-gray-500 dark:text-gray-400">{key.keyPrefix}...</td>
                            <td className="py-2 pr-4 text-gray-500 dark:text-gray-400">
                              {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString('fr-CH') : 'Jamais'}
                            </td>
                            <td className="py-2 pr-4">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${key.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700'}`}>
                                {key.isActive ? 'Active' : 'Révoquée'}
                              </span>
                            </td>
                            <td className="py-2">
                              {key.isActive && (
                                <button
                                  onClick={() => handleRevokeApiKey(key.id)}
                                  className="text-red-600 hover:text-red-800 dark:text-red-400 text-xs underline"
                                >
                                  Révoquer
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </PlanGate>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
